import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { PERMISSIONS } from '@wms/shared';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../lib/api';
import { useSession } from '../../lib/session';
import { ExpiryBadge } from './expiry-badge';
import { formatQty } from './format-qty';
import { InboundDrawer } from './inbound-drawer';
import { OutboundDrawer } from './outbound-drawer';
import { TransferDrawer } from './transfer-drawer';
import { SkeletonBlock, SkeletonRows } from '../skeleton';
import type {
  BatchDetail,
  ByLocationRow,
  ByProductRow,
  LocationStockItem,
  SearchBatch,
} from './types';

const ROW_HEIGHT = 56;
const PAGE_SIZE = 100;

type ViewMode = 'byProduct' | 'byLocation';

/**
 * 库存浏览器（S6，M5 交付按位置/按产品两个视角 + 批次卡片）。
 *
 * R-18 在 UI 上的落点：按位置视角与位置库存抽屉**只显示物理在手**，
 * 不渲染任何"可用量"列；在手/预留/可用三个数字只出现在产品汇总
 * （批次级求和）与批次卡片上。
 */
export function InventoryPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { data: session } = useSession();
  const canInbound = session?.permissions.includes(PERMISSIONS.INVENTORY_INBOUND) ?? false;
  const canOutbound = session?.permissions.includes(PERMISSIONS.INVENTORY_OUTBOUND) ?? false;
  const canTransfer = session?.permissions.includes(PERMISSIONS.INVENTORY_TRANSFER) ?? false;
  const [view, setView] = useState<ViewMode>('byProduct');
  const [query, setQuery] = useState('');
  const [inboundOpen, setInboundOpen] = useState(false);
  const [outboundOpen, setOutboundOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [locationDrawer, setLocationDrawer] = useState<ByLocationRow | null>(null);
  const [batchDrawer, setBatchDrawer] = useState<string | null>(null);

  return (
    <div>
      <div className="row row--between">
        <h1 style={{ fontSize: 'var(--text-2xl)' }}>{t('nav.inventory')}</h1>
        <div className="row" style={{ gap: 'var(--space-2)' }}>
          {canTransfer && (
            <button className="btn btn--ghost btn--sm" onClick={() => setTransferOpen(true)}>
              {t('inventory.transferAction')}
            </button>
          )}
          {canOutbound && (
            <button className="btn btn--ghost btn--sm" onClick={() => setOutboundOpen(true)}>
              {t('inventory.outboundAction')}
            </button>
          )}
          {canInbound && (
            <button className="btn btn--primary btn--sm" onClick={() => setInboundOpen(true)}>
              {t('inventory.inboundAction')}
            </button>
          )}
        </div>
      </div>

      <div className="row" style={{ gap: 'var(--space-2)', margin: 'var(--space-3) 0' }}>
        <button
          className={view === 'byProduct' ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
          onClick={() => setView('byProduct')}
        >
          {t('inventory.viewByProduct')}
        </button>
        <button
          className={view === 'byLocation' ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
          onClick={() => setView('byLocation')}
        >
          {t('inventory.viewByLocation')}
        </button>
        {view === 'byProduct' && (
          <input
            className="field"
            style={{ flex: 1, maxWidth: '360px' }}
            placeholder={t('products.searchPlaceholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        )}
      </div>

      {view === 'byProduct' ? (
        <ByProductTable query={query} onOpenBatch={(id) => setBatchDrawer(id)} />
      ) : (
        <ByLocationTable onOpenLocation={setLocationDrawer} />
      )}

      {inboundOpen && <InboundDrawer onClose={() => setInboundOpen(false)} />}
      {outboundOpen && <OutboundDrawer onClose={() => setOutboundOpen(false)} />}
      {transferOpen && <TransferDrawer onClose={() => setTransferOpen(false)} />}
      {locationDrawer && (
        <LocationStockDrawer
          row={locationDrawer}
          onClose={() => setLocationDrawer(null)}
          onOpenBatch={(id) => setBatchDrawer(id)}
        />
      )}
      {batchDrawer && (
        <BatchCardDrawer batchId={batchDrawer} onClose={() => setBatchDrawer(null)} />
      )}
    </div>
  );
}

function useInfiniteRows<T>(key: readonly unknown[], path: string) {
  return useInfiniteQuery({
    queryKey: ['inventory', ...key],
    queryFn: ({ pageParam }) =>
      apiFetch<{ items: T[]; nextCursor: string | null }>(
        `${path}${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`,
      ),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

function ByProductTable({
  query,
  onOpenBatch,
}: {
  readonly query: string;
  readonly onOpenBatch: (batchId: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<ByProductRow | null>(null);
  const { data, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteRows<ByProductRow>(
      ['by-product', query],
      `/inventory/by-product?limit=${PAGE_SIZE}${query ? `&query=${encodeURIComponent(query)}` : ''}`,
    );
  const items = useMemo(() => (data?.pages ?? []).flatMap((p) => p.items), [data]);

  const parentRef = useRef<HTMLDivElement>(null);
  const lastIndexRef = useRef(0);
  useEffect(() => {
    const el = parentRef.current;
    if (!el) return;
    const onScroll = (): void => {
      lastIndexRef.current = Math.floor((el.scrollTop + el.clientHeight) / ROW_HEIGHT);
      if (lastIndexRef.current >= items.length - 10 && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [items.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isPending) return <SkeletonRows rows={8} />;
  if (items.length === 0) {
    return (
      <div className="empty-state">
        <p>{query ? t('empty.noResults') : t('inventory.emptyByProduct')}</p>
      </div>
    );
  }

  return (
    <>
      {/* 【对齐修复】表头与数据行此前分用 .row（gap 12px、无横向 padding）与
          .tree__row（gap 4px、横向 padding 8px），7 列累计漂移越来越大——
          换成共享 .data-row/.data-cell 原语后两者的盒模型天然一致。 */}
      <div className="table-scroll">
        <div style={{ minWidth: '980px' }}>
          <div className="data-row data-row--head">
            <span className="data-cell" style={{ flex: '2 1 0', minWidth: 0 }}>
              {t('products.colName')}
            </span>
            <span className="data-cell" style={{ flex: '1 1 0', minWidth: 0 }}>
              {t('products.colSku')}
            </span>
            {/* 【用户反馈】全列左对齐：右对齐让短数值悬在长表头右端，
                视觉上"不在表头正下方"，被读成偏移 */}
            <span className="data-cell" style={{ width: '140px' }}>
              {t('inventory.colOnHandWh')}
            </span>
            <span className="data-cell" style={{ width: '140px' }}>
              {t('inventory.colReservedAll')}
            </span>
            <span className="data-cell" style={{ width: '140px' }}>
              {t('inventory.colAvailableAll')}
            </span>
            <span className="data-cell" style={{ width: '70px' }}>
              {t('inventory.colBatches')}
            </span>
            <span className="data-cell" style={{ width: '70px' }}>
              {t('inventory.colLocations')}
            </span>
            <span className="data-cell" style={{ width: '120px' }}>
              {t('inventory.colEarliestExpiry')}
            </span>
          </div>
          <div ref={parentRef} style={{ maxHeight: '560px', overflow: 'auto' }}>
            {items.map((row) => (
              <div
                key={row.productId}
                className="data-row"
                style={{ height: `${ROW_HEIGHT}px` }}
                onClick={() => setExpanded(row)}
              >
                <span className="data-cell" style={{ flex: '2 1 0', minWidth: 0 }} title={row.name}>
                  {row.name}
                </span>
                <span className="data-cell" style={{ flex: '1 1 0', minWidth: 0 }} title={row.sku}>
                  {row.sku}
                </span>
                <span
                  className="data-cell"
                  style={{ width: '140px' }}
                  title={`${row.onHandBase} ${row.baseUnitCode}`}
                >
                  {formatQty(row.onHandBase, row.units, row.baseUnitCode)}
                </span>
                <span
                  className="data-cell"
                  style={{ width: '140px' }}
                  title={`${row.reservedBase} ${row.baseUnitCode}`}
                >
                  {formatQty(row.reservedBase, row.units, row.baseUnitCode)}
                </span>
                <span
                  className="data-cell"
                  style={{ width: '140px' }}
                  title={`${row.availableBase} ${row.baseUnitCode}`}
                >
                  {formatQty(row.availableBase, row.units, row.baseUnitCode)}
                </span>
                <span className="data-cell" style={{ width: '70px' }}>
                  {row.batchCount}
                </span>
                <span className="data-cell" style={{ width: '70px' }}>
                  {row.locationCount}
                </span>
                <span className="data-cell" style={{ width: '120px', overflow: 'visible' }}>
                  {row.earliestExpiration ? (
                    <ExpiryBadge expirationDate={row.earliestExpiration} hasExpiration />
                  ) : (
                    '—'
                  )}
                </span>
              </div>
            ))}
            {isFetchingNextPage && <p>{t('app.loading')}</p>}
          </div>
        </div>
      </div>
      {expanded && (
        <ProductBatchesDrawer
          row={expanded}
          onClose={() => setExpanded(null)}
          onOpenBatch={onOpenBatch}
        />
      )}
    </>
  );
}

function ByLocationTable({
  onOpenLocation,
}: {
  readonly onOpenLocation: (row: ByLocationRow) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { data, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteRows<ByLocationRow>(['by-location'], `/inventory/by-location?limit=${PAGE_SIZE}`);
  const items = useMemo(() => (data?.pages ?? []).flatMap((p) => p.items), [data]);

  if (isPending) return <SkeletonRows rows={8} />;
  if (items.length === 0) {
    return (
      <div className="empty-state">
        <p>{t('inventory.emptyByLocation')}</p>
      </div>
    );
  }

  return (
    <div className="table-scroll">
      <div style={{ minWidth: '640px' }}>
      <div className="data-row data-row--head">
        <span className="data-cell" style={{ flex: '3 1 0', minWidth: 0 }}>
          {t('inventory.colLocationPath')}
        </span>
        <span className="data-cell" style={{ width: '90px' }}>
          {t('inventory.colBatches')}
        </span>
        {/* R-18：位置视角只有物理在手，没有"可用"列 */}
        <span className="data-cell" style={{ width: '130px' }}>
          {t('stock.onHandAtLocation')}
        </span>
        <span className="data-cell" style={{ width: '110px' }} />
      </div>
      {items.map((row) => (
        <div key={row.locationId} className="data-row" onClick={() => onOpenLocation(row)}>
          <span className="data-cell" style={{ flex: '3 1 0', minWidth: 0 }} title={row.pathLabel}>
            {row.pathLabel}
          </span>
          <span className="data-cell" style={{ width: '90px' }}>
            {row.batchCount}
          </span>
          {/* 跨产品求和，无统一单位可换算——保持基础数量 */}
          <span className="data-cell" style={{ width: '130px' }}>
            {row.onHandBase}
          </span>
          <span className="data-cell" style={{ width: '110px', overflow: 'visible' }}>
            {row.hasNegative && (
              <span className="badge badge--danger">{t('status.negativeStock')}</span>
            )}
          </span>
        </div>
      ))}
      {hasNextPage && (
        <button
          className="btn btn--ghost btn--sm"
          disabled={isFetchingNextPage}
          onClick={() => void fetchNextPage()}
        >
          {t('app.loadMore')}
        </button>
      )}
      </div>
    </div>
  );
}

/** 产品行点开：该产品的批次卡片（数据来自全局搜索端点的同一结构，按 SKU 精确召回） */
function ProductBatchesDrawer({
  row,
  onClose,
  onOpenBatch,
}: {
  readonly row: ByProductRow;
  readonly onClose: () => void;
  readonly onOpenBatch: (batchId: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { data, isPending } = useQuery({
    queryKey: ['inventory', 'product-batches', row.productId],
    queryFn: async () => {
      const res = await apiFetch<{
        items: { productId: string; batches: SearchBatch[] }[];
      }>(`/search?q=${encodeURIComponent(row.sku)}`);
      return res.items.find((i) => i.productId === row.productId)?.batches ?? [];
    },
  });

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <aside className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer__header">
          <h2 className="drawer__title">{row.name}</h2>
          <button className="btn btn--ghost btn--sm" onClick={onClose}>
            ✕
          </button>
        </div>
        <div style={{ padding: 'var(--space-4)', overflowY: 'auto' }}>
          <div className="row" style={{ gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
            <span>
              {t('stock.onHand')}{' '}
              <strong>{formatQty(row.onHandBase, row.units, row.baseUnitCode)}</strong>
            </span>
            <span>
              {t('stock.reserved')}{' '}
              <strong>{formatQty(row.reservedBase, row.units, row.baseUnitCode)}</strong>
            </span>
            <span>
              {t('stock.available')}{' '}
              <strong>{formatQty(row.availableBase, row.units, row.baseUnitCode)}</strong>
            </span>
          </div>
          {isPending && <SkeletonRows rows={3} rowHeight={36} bars={4} />}
          {(data ?? []).map((batch) => (
            <button
              key={batch.batchId}
              type="button"
              className="card"
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: 'var(--space-3)',
                marginBottom: 'var(--space-2)',
                cursor: 'pointer',
              }}
              onClick={() => onOpenBatch(batch.batchId)}
            >
              <div className="row" style={{ gap: 'var(--space-2)' }}>
                <strong>{batch.batchNo}</strong>
                <span>{formatQty(batch.onHandBase, row.units, row.baseUnitCode)}</span>
                <ExpiryBadge
                  expirationDate={batch.expirationDate}
                  hasExpiration={batch.hasExpiration}
                />
              </div>
            </button>
          ))}
        </div>
      </aside>
    </div>
  );
}

/** 位置行点开：该位置的批次列表（S5 库存标签页；R-18 只显示物理在手） */
function LocationStockDrawer({
  row,
  onClose,
  onOpenBatch,
}: {
  readonly row: ByLocationRow;
  readonly onClose: () => void;
  readonly onOpenBatch: (batchId: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { data, isPending } = useQuery({
    queryKey: ['inventory', 'location-stock', row.locationId],
    queryFn: () =>
      apiFetch<{ items: LocationStockItem[] }>(`/inventory/locations/${row.locationId}/stock`),
  });

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <aside className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer__header">
          <h2 className="drawer__title">{row.pathLabel}</h2>
          <button className="btn btn--ghost btn--sm" onClick={onClose}>
            ✕
          </button>
        </div>
        <div style={{ padding: 'var(--space-4)', overflowY: 'auto' }}>
          <p className="field-hint" style={{ marginBottom: 'var(--space-2)' }}>
            {t('stock.onHandAtLocation')}
          </p>
          {isPending && <SkeletonRows rows={3} rowHeight={36} bars={4} />}
          {(data?.items ?? []).map((item) => (
            <button
              key={item.batchId}
              type="button"
              className="tree__row"
              style={{ width: '100%', cursor: 'pointer' }}
              onClick={() => onOpenBatch(item.batchId)}
            >
              <span style={{ flex: 2 }}>{item.productName}</span>
              <span style={{ width: '130px' }}>{item.batchNo}</span>
              <span style={{ width: '110px', textAlign: 'right' }}>
                {formatQty(item.onHandBase, item.units)}
              </span>
              <span style={{ width: '120px' }}>
                <ExpiryBadge
                  expirationDate={item.expirationDate}
                  hasExpiration={item.hasExpiration}
                />
              </span>
              <span style={{ width: '110px' }}>
                {BigInt(item.batchReservedBase) > 0n && (
                  <span
                    className="badge badge--info"
                    title={t('inventory.batchReservedHint', { qty: item.batchReservedBase })}
                  >
                    {t('status.hasReservation')}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      </aside>
    </div>
  );
}

/** 批次卡片（roadmap M5"批次卡片"）：身份 + 三个精确数字 + 当前分布（D-06 热路径） */
function BatchCardDrawer({
  batchId,
  onClose,
}: {
  readonly batchId: string;
  readonly onClose: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const { data, isPending } = useQuery({
    queryKey: ['inventory', 'batch', batchId],
    queryFn: () => apiFetch<BatchDetail>(`/inventory/batches/${batchId}`),
  });
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' });

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <aside className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer__header">
          <h2 className="drawer__title">{data?.batchNo ?? t('app.loading')}</h2>
          <button className="btn btn--ghost btn--sm" onClick={onClose}>
            ✕
          </button>
        </div>
        {isPending || !data ? (
          <div style={{ padding: 'var(--space-4)' }}>
            <SkeletonBlock height={72} />
            <SkeletonRows rows={4} rowHeight={36} bars={4} />
          </div>
        ) : (
          <div style={{ padding: 'var(--space-4)', overflowY: 'auto' }}>
            <p>
              <strong>{data.productName}</strong> <span className="field-hint">SKU {data.sku}</span>
            </p>
            <div className="row" style={{ gap: 'var(--space-3)', margin: 'var(--space-3) 0' }}>
              <span>
                {t('stock.onHand')} <strong>{formatQty(data.onHandBase, data.units)}</strong>
              </span>
              <span>
                {t('stock.reserved')} <strong>{formatQty(data.reservedBase, data.units)}</strong>
              </span>
              <span>
                {t('stock.available')} <strong>{formatQty(data.availableBase, data.units)}</strong>
              </span>
            </div>
            <dl>
              <div className="row row--between">
                <dt className="field-hint">{t('inventory.batchExpiry')}</dt>
                <dd>
                  <ExpiryBadge
                    expirationDate={data.expirationDate}
                    hasExpiration={data.hasExpiration}
                  />
                </dd>
              </div>
              {data.externalLotNo && (
                <div className="row row--between">
                  <dt className="field-hint">{t('inbound.externalLot')}</dt>
                  <dd>{data.externalLotNo}</dd>
                </div>
              )}
              {data.supplierName && (
                <div className="row row--between">
                  <dt className="field-hint">{t('inbound.supplier')}</dt>
                  <dd>{data.supplierName}</dd>
                </div>
              )}
              <div className="row row--between">
                <dt className="field-hint">{t('inventory.receivedAt')}</dt>
                <dd>{fmt.format(new Date(data.receivedAt))}</dd>
              </div>
              {data.originDocNo && (
                <div className="row row--between">
                  <dt className="field-hint">{t('inventory.originDoc')}</dt>
                  <dd>{data.originDocNo}</dd>
                </div>
              )}
            </dl>
            <h3 style={{ margin: 'var(--space-3) 0 var(--space-2)' }}>
              {t('inventory.distribution')}
            </h3>
            {data.distribution.map((d) => (
              <div key={d.locationId} className="row" style={{ gap: 'var(--space-2)' }}>
                <span style={{ flex: 3 }}>
                  {d.warehouseName} › {d.pathLabel}
                </span>
                <span style={{ width: '130px', textAlign: 'right' }}>
                  {formatQty(d.onHandBase, data.units)}
                </span>
              </div>
            ))}
          </div>
        )}
      </aside>
    </div>
  );
}
